import PropTypes from "prop-types";
import { Component, createRef, Fragment } from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import split from "lodash/split";
import { FormattedMessage, injectIntl } from "react-intl";
import { propTypes } from "redux-form";
import {
	BOOK_EXTERNAL_PAYMENT_STATUS,
	BOOK_STATUS,
	EXTERNAL_PAYMENT_STATUS,
	FRANCE_DOM_COUNTRIES,
	OFFER_CONTRACT_TYPES,
	PASSENGER_TYPE,
	PAYMENT_COUPON_TYPE,
	PAYMENT_METHODS,
	PAYMENT_OPTIONS,
	RESOLUTION,
	TRANSPORTATION_TYPES,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import { scrollToElement } from "app/utils/scroller";
import AlertMessage from "app/pages/.shared/AlertMessage.jsx";
import {
	contactFields,
	insuranceField,
	passengerFields,
	paymentFields,
	termsField,
} from "app/pages/Booking/Payment/paymentSchema";
import RelativeLink from "app/pages/.shared/RelativeLink";
import { sendTagOnInsuranceSelect, sendTagOnPaymentInputBlur } from "app/utils/analytics";
import ContactOurExperts from "app/pages/.shared/ContactOurExperts";
import PriceChangedWarningMessage from "app/pages/Booking/Payment/PriceChangedWarningMessage";
import "./payment.scss";
import { paymentTypesValues } from "app/utils/propTypes";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import Modal from "react-modal";
import PaymentContactContainer from "app/pages/Booking/Payment/PaymentContactContainer";
import PaymentCouponsContainer from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponsContainer";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";
import { isExternalPaymentError, isPaymentError } from "app/utils/paymentUtils";
import PaymentInsurancesSection from "app/pages/Booking/Payment/PaymentInsurancesSection";
import PaymentInfoSection from "app/pages/Booking/Payment/PaymentInfoSection";
import PaymentPassengersSection from "app/pages/Booking/Payment/PaymentPassengersSection/PaymentPassengersSection";
import PaymentCTASection from "app/pages/Booking/Payment/PaymentCTASection/PaymentCTASection";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";

const MODAL_STYLE = {
	overlay: {
		backgroundColor: "rgba(43, 56, 63, .65)",
		zIndex: 6,
	},
	content: {
		top: 0,
		right: 0,
		left: 0,
		bottom: 0,
		border: "none",
		background: "none",
		overflow: "hidden",
		padding: "50px",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
};

class Payment extends Component {
	constructor() {
		super();
		this.contactRef = createRef();
		this.paymentErrorMessageRef = createRef();
		this.paymentMethodsErrorMessageRef = createRef();
		this.insuranceErrorMessage = createRef();
		this.onCountryChange = this.onCountryChange.bind(this);
		this.scrollToErrorMessage = this.scrollToErrorMessage.bind(this);
		this.handleInputBlur = this.handleInputBlur.bind(this);
		this.handleKeyPressSubmit = this.handleKeyPressSubmit.bind(this);
		this.selectCouponTypePromo = this.selectCouponTypePromo.bind(this);
		this.onInsuranceSelect = this.onInsuranceSelect.bind(this);
	}

	handleKeyPressSubmit(event) {
		if (event.key === "Enter") {
			event.preventDefault(); //permet d'arrêter le submit
		}
	}

	handleInputBlur(event) {
		let value = event.target.value;

		if (event.target.type === "checkbox") {
			value = event.target.checked;
		}

		if (value || value === false) {
			sendTagOnPaymentInputBlur(event.target.name);
		}
	}

	onCountryChange(event) {
		// Si le user a selectionné casino alors que celui-ci n'est pas autorisé, alors on sélectionne le paiement par CB par défaut
		if (
			FRANCE_DOM_COUNTRIES.indexOf(event.target.value) === -1 &&
			this.props.fields.payment.type.value === PAYMENT_METHODS.CASINO
		) {
			this.props.fields.payment.type.onChange(PAYMENT_METHODS.CARD);
			this.props.fields.payment.mode.onChange(String(PAYMENT_OPTIONS["1x"]));

			this.props.updateBooking({
				paymentType: PAYMENT_METHODS.CARD,
				paymentMode: PAYMENT_OPTIONS["1x"],
			});
		}
	}

	selectCouponTypePromo() {
		this.props.updateBooking({
			coupon: PAYMENT_COUPON_TYPE.PROMOCODE,
		});
	}

	onInsuranceSelect(data, insurance) {
		const { updateBooking } = this.props;

		// permet entre autre de mettre à jour le montant total de la réservation à la sélection de l'assurance
		updateBooking(data);

		sendTagOnInsuranceSelect(insurance);
	}

	scrollToErrorMessage(errors) {
		let errorMessageRef;
		const contactErrorList = [];
		const paymentErrorList = [];
		const insuranceErrorList = [];
		const termsErrorList = [];
		const passengersErrorList = [];

		contactFields.forEach(path => {
			const error = get(errors, path);
			if (error) {
				contactErrorList.push(error);
			}
		});

		if (contactErrorList.length > 0) {
			errorMessageRef = this.contactRef.current;
		} else {
			passengerFields.forEach(path => {
				this.props.fields.passengers.forEach((passenger, index) => {
					const parts = split(path, "[]");
					const error = get(errors, `${parts[0]}[${index}]${parts[1]}`);
					if (error) {
						passengersErrorList.push(error);
					}
				});
			});

			if (passengersErrorList.length > 0) {
				errorMessageRef = this.refs.passengersErrorMessage;
			} else {
				insuranceField.forEach(path => {
					const error = get(errors, path);
					if (error) {
						insuranceErrorList.push(error);
					}
				});

				if (insuranceErrorList.length > 0) {
					errorMessageRef = this.insuranceErrorMessage.current;
				} else {
					paymentFields.forEach(path => {
						const error = get(errors, path);
						if (error) {
							paymentErrorList.push(error);
						}
					});

					if (paymentErrorList.length > 0) {
						errorMessageRef =
							this.paymentMethodsErrorMessageRef &&
							this.paymentMethodsErrorMessageRef.current;
					} else {
						termsField.forEach(path => {
							const error = get(errors, path);
							if (error) {
								termsErrorList.push(error);
							}
						});

						if (termsErrorList.length > 0) {
							errorMessageRef = this.refs.termsErrorMessage;
						}
					}
				}
			}
		}

		if (errorMessageRef) {
			scrollToElement({ element: errorMessageRef, offset: -100 });
		}
	}

	componentWillUpdate(nextProps) {
		const errors = nextProps.errors;
		const bookStatus = nextProps.payment.bookStatus;

		if (
			bookStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ABORTED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_3DS_REFUSED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_REFUSED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_INFO ||
			bookStatus === BOOK_STATUS.PAYMENT_AGREEMENT_REFUSED ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_TECHNICAL_ERROR ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_UNKNOWN ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_REFUSED ||
			bookStatus === BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR
		) {
			if (this.paymentErrorMessageRef && this.paymentErrorMessageRef.current) {
				scrollToElement({ element: this.paymentErrorMessageRef.current, offset: -100 });
			}
		} else if (isEmpty(this.props.errors) && errors) {
			// on scroll vers le premier éventuel bloc de message d'erreur
			// TODO a optimiser ou refactorer
			if (errors && !isEmpty(errors)) {
				this.scrollToErrorMessage(errors);
			} else if (nextProps.error) {
				scrollToElement({ element: this.refs.errorMessage, offset: -100 });
			}
		}
	}

	render() {
		const {
			fields = {},
			handleSubmit,
			submitting,
			booking = {},
			payment = {},
			error, // set to undefined on each redux-form/CHANGE
			errors,
			resolution,
			productUri,
			dateOfBirthRequired,
			isChildrenAgeRequired,
			showFrequentTravellerCardInput,
			intl,
			selectedPaymentType,
			shop = "",
			showInsurancesSection,
			onFetchProfilSuccess,
			onFetchProfilFail,
			assuranceMultirisquesSansMotifDocumentName,
			assuranceAnnulationSansMotifDocumentName,
			cartAmount,
			usableCouponsStatus,
			isOperatorMode,
			brandDisplayName,
			rgpdMessagePayment = [],
			shouldGetProfileAtPayment,
			priceChangeData = {},
			isUsaDestination = false,
		} = this.props;

		const {
			contact: { name, address, email, phone },
			passengers,
			terms,
		} = fields;

		const paymentErrorList = [];
		const insuranceErrorList = [];
		const termsErrorList = [];
		const passengersErrorList = [];

		paymentFields.forEach(path => {
			const error = get(errors, path);

			if (
				error &&
				((selectedPaymentType &&
					((path.match(/\.card\./) && selectedPaymentType === PAYMENT_METHODS.CARD) ||
						(path.match(/issuerId/) && selectedPaymentType === PAYMENT_METHODS.IDEAL) ||
						(path.match(/\.cardBCMC\./) &&
							selectedPaymentType === PAYMENT_METHODS.BCMC) ||
						(path.match(/\.swissBilling\./) &&
							selectedPaymentType === PAYMENT_METHODS.SWISS_BILLING))) ||
					path.match(/\.card\./))
			) {
				paymentErrorList.push(intl.formatMessage(error));
			}
		});

		insuranceField.forEach(path => {
			const error = get(errors, path);
			if (error) {
				insuranceErrorList.push(intl.formatMessage(error));
			}
		});

		termsField.forEach(path => {
			const error = get(errors, path);
			if (error) {
				termsErrorList.push(intl.formatMessage(error));
			}
		});

		passengerFields.forEach(path => {
			fields.passengers.forEach((passenger, index) => {
				const parts = split(path, "[]");
				const error = get(errors, `${parts[0]}[${index}]${parts[1]}`);
				if (error) {
					const type = passenger.type;
					let errorPassengerID;

					if (type.value === PASSENGER_TYPE.CHILD) {
						errorPassengerID = "payment.passengers.child";
					} else if (type.value === PASSENGER_TYPE.INFANT) {
						errorPassengerID = "payment.passengers.infant";
					} else {
						errorPassengerID = "payment.passengers.adult";
					}
					passengersErrorList.push(
						`${intl.formatMessage(
							{ id: errorPassengerID },
							{ index: index + 1 }
						)}: ${intl.formatMessage(error)}`
					);
				}
			});
		});

		const bookStatus = payment.bookStatus;

		const isPassengersError =
			bookStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY ||
			bookStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY;

		const isNothingToPay = cartAmount === 0;
		const isCouponsLoading =
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS;

		const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
		return (
			<Fragment>
				<form
					className="payment"
					ref="errorMessage"
					onSubmit={handleSubmit}
					onKeyPress={this.handleKeyPressSubmit}
				>
					{isMobile && (
						<div className="payment__my-booking">
							<HolidaySummaryContainer showProductPicture />
						</div>
					)}

					{error &&
						isEmpty(errors) &&
						!isPaymentError(bookStatus) &&
						!isExternalPaymentError(bookStatus) &&
						!isPassengersError && (
							<AlertMessage
								message={
									<FormattedMessage
										values={{
											link: (
												<RelativeLink
													to={{ pathname: "/booking/quote" }}
													variant="naked"
												>
													<FormattedMessage id="error.no.avaibilities.link.label" />
												</RelativeLink>
											),
										}}
										{...error}
									/>
								}
							/>
						)}

					{!isMobile && priceChangeData.isPriceChange && (
						<PriceChangedWarningMessage
							isTravelingByTrain={
								booking?.flight?.type === TRANSPORTATION_TYPES.TRAIN
							}
							offerType={booking?.offer?.type}
							newPrice={priceChangeData?.newPrice}
							oldPrice={priceChangeData?.oldPrice}
						/>
					)}

					{bookStatus === BOOK_STATUS.WARNING_FULL_PROPERTY ? (
						<ContactOurExperts
							message={<FormattedMessage id="confirmation.contact.headline.short" />}
						/>
					) : (
						<Fragment>
							<Fragment>
								<div
									className="payment__item"
									style={{ order: isOperatorMode ? "1" : "0" }}
								>
									<Typography
										variant={TYPOGRAPHY_VARIANTS.XL3}
										variantMD={TYPOGRAPHY_VARIANTS.XL4}
										isBold
										className="payment__title"
										component="h2"
									>
										<FormattedMessage id="payment.contact.title" />
									</Typography>

									<div className="payment__content" ref={this.contactRef}>
										<PaymentContactContainer
											initializeForm={this.props.initializeForm}
											formInitialValues={this.props.formInitialValues}
											addressField={address}
											errors={errors}
											emailField={email}
											handleInputBlur={this.handleInputBlur}
											nameField={name}
											onCountryChange={this.onCountryChange}
											phoneField={phone}
											onFetchProfilSuccess={onFetchProfilSuccess}
											onFetchProfilFail={onFetchProfilFail}
											shouldGetProfileAtPayment={shouldGetProfileAtPayment}
										/>
									</div>
								</div>

								{get(booking, "offer.contractType") !==
									OFFER_CONTRACT_TYPES.RENTAL && (
									<div
										className="payment__item"
										ref="passengersErrorMessage"
										style={{ order: "2" }}
									>
										<PaymentPassengersSection
											passengersErrorList={passengersErrorList}
											bookStatus={bookStatus}
											resolution={resolution}
											productUri={productUri}
											dateOfBirthRequired={dateOfBirthRequired}
											showFrequentTravellerCardInput={
												showFrequentTravellerCardInput
											}
											isChildrenAgeRequired={isChildrenAgeRequired}
											onBlur={this.handleInputBlur}
											passengers={passengers}
											error={error}
											showError={error && isPassengersError}
											showInsurancesSection={showInsurancesSection}
											warningMessage={isUsaDestination}
										/>
									</div>
								)}

								{showInsurancesSection && (
									<div
										className="payment__item"
										style={{ order: isOperatorMode ? "0" : "3" }}
									>
										<PaymentInsurancesSection
											insuranceErrorList={insuranceErrorList}
											payment={payment}
											resolution={resolution}
											shop={shop}
											assuranceMultirisquesSansMotifDocumentName={
												assuranceMultirisquesSansMotifDocumentName
											}
											assuranceAnnulationSansMotifDocumentName={
												assuranceAnnulationSansMotifDocumentName
											}
											total={this.props.total}
											fields={fields}
											refInsuranceErrorMessage={this.insuranceErrorMessage}
											onInsuranceSelect={this.onInsuranceSelect}
										/>
									</div>
								)}

								<div className="payment__item" style={{ order: "4" }}>
									<PaymentCouponsContainer field={fields.coupon} />
								</div>

								{(!isNothingToPay || isCouponsLoading) && (
									<div className="payment__item" style={{ order: "5" }}>
										<PaymentInfoSection
											brandDisplayName={brandDisplayName}
											usableCouponsStatus={usableCouponsStatus}
											payment={payment}
											shop={shop}
											selectedPaymentType={selectedPaymentType}
											paymentErrorList={paymentErrorList}
											fields={fields}
											error={error}
											paymentErrorMessageRef={this.paymentErrorMessageRef}
											paymentMethodsErrorMessageRef={
												this.paymentMethodsErrorMessageRef
											}
											handleInputBlur={this.handleInputBlur}
											booking={this.props.booking}
										/>
									</div>
								)}
							</Fragment>
							<div
								className="payment__pay-button"
								style={{ order: "6" }}
								ref="termsErrorMessage"
							>
								<PaymentCTASection
									termsErrorList={termsErrorList}
									onClick={() => {
										this.scrollToErrorMessage(this.props.errors);
									}}
									cartAmount={cartAmount}
									paymentType={selectedPaymentType}
									termsField={terms}
									brandDisplayName={brandDisplayName}
									onPaymentTermsBlur={this.handleInputBlur}
									shouldDisableButton={submitting}
									rgpdMessagePayment={rgpdMessagePayment}
								/>
							</div>
						</Fragment>
					)}
				</form>
				<PushNotificationContainer />
				<Modal isOpen={bookStatus === BOOK_STATUS.LOADING} style={MODAL_STYLE}>
					<div className="payment-modal__loading-placeholder">
						<LoadingPlaceholder
							message={<FormattedMessage id="payment.external.wait.message" />}
						/>
					</div>
				</Modal>
			</Fragment>
		);
	}
}

Payment.propTypes = {
	...propTypes,
	profile: PropTypes.object,
	cartAmount: PropTypes.number,
	intl: PropTypes.object,
	usableCouponsStatus: PropTypes.string,
	partnerCode: PropTypes.string,
	vpSaleCode: PropTypes.string,
	shop: PropTypes.string,
	updateBooking: PropTypes.func,
	onFetchProfilSuccess: PropTypes.func,
	onFetchProfilFail: PropTypes.func,
	showFrequentTravellerCardInput: PropTypes.bool,
	selectedPaymentType: paymentTypesValues,
	showInsurancesSection: PropTypes.bool,
	assuranceMultirisquesSansMotifDocumentName: PropTypes.string,
	assuranceAnnulationSansMotifDocumentName: PropTypes.string,
	isOperatorMode: PropTypes.bool,
	brandDisplayName: PropTypes.string,
	rgpdMessagePayment: PropTypes.array,
	shouldGetProfileAtPayment: PropTypes.bool,
	priceChangeData: PropTypes.object,
	isUsaDestination: PropTypes.bool,
};

export default injectIntl(Payment);
